import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Layout from '../../../components/vision/Layout';
import ProtectedRoute from '../../../components/vision/ProtectedRoute';

// Cliente Supabase dedicado para propostas (completamente isolado do AuthContext)
const supabasePropostas = createClient(
  'https://cymjsqoqnxexcefogbey.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN5bWpzcW9xbnhleGNlZm9nYmV5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg0MDExMTAsImV4cCI6MjA4Mzk3NzExMH0.HiEOWs0mHOX-knImGJmh3Oj8fcDGodY3SLQHIkviZeY',
  {
    auth: {
      persistSession: false, // NÃO persistir sessão (evita conflito)
      autoRefreshToken: false, // NÃO auto-refresh
      detectSessionInUrl: false, // NÃO detectar sessão na URL
      storageKey: 'supabase-propostas' // Storage separado
    }
  }
);

interface CondicaoEspecial {
  tipo: 'trafego' | 'crm';
  identificador: string;
  plano_nome: string;
  valor_original: number;
  valor_desconto: number;
  aplicar_primeiros_meses: number | null;
  descricao: string;
}

interface Proposta {
  id: string;
  nome_empresa: string;
  link_hash: string;
  created_at: string;
  updated_at: string;
  status: 'ativa' | 'visualizada' | 'aceita' | 'recusada';
  visualizacoes: number;
  ultima_visualizacao: string | null;
  planos_trafego: any[];
  planos_crm: any[];
  total_unico: number;
  total_mensal: number;
  condicoes_especiais?: {
    planos_com_desconto: CondicaoEspecial[];
  };
  data_limite_proposta?: string;
}

export default function PropostasPage() {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [selectedProposta, setSelectedProposta] = useState<Proposta | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCondicoesModal, setShowCondicoesModal] = useState(false);
  const [condicoes, setCondicoes] = useState<CondicaoEspecial[]>([]);
  const [dataLimite, setDataLimite] = useState<string>('');
  const [loadingIA, setLoadingIA] = useState(false);
  const [sugestoesIA, setSugestoesIA] = useState<string[]>([]);
  const [showSugestoesModal, setShowSugestoesModal] = useState(false);
  const [condicaoAtualIA, setCondicaoAtualIA] = useState<{index: number, condicao: CondicaoEspecial} | null>(null);

  useEffect(() => {
    carregarPropostas();
  }, []);

  const carregarPropostas = async () => {
    try {
      console.log('📋 Carregando propostas com cliente dedicado...');
      setLoading(true);
      
      const { data, error } = await supabasePropostas
        .from('propostas')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('📊 Resposta:', { data, error });

      if (error) {
        console.error('❌ Erro:', error);
        throw error;
      }
      
      console.log('✅ Propostas carregadas:', data?.length || 0);
      setPropostas(data || []);
    } catch (error) {
      console.error('❌ Erro ao carregar propostas:', error);
      setPropostas([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPropostas = propostas.filter(proposta => {
    const matchSearch = proposta.nome_empresa.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'todos' || proposta.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      ativa: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Ativa' },
      visualizada: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Visualizada' },
      aceita: { bg: 'bg-green-100', text: 'text-green-700', label: 'Aceita' },
      recusada: { bg: 'bg-red-100', text: 'text-red-700', label: 'Recusada' }
    };
    const badge = badges[status as keyof typeof badges] || badges.ativa;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getLinkProposta = (hash: string) => {
    // Sempre usar o domínio correto de propostas
    const isLocal = window.location.hostname === 'localhost';
    const baseUrl = isLocal ? 'http://localhost:5174' : 'https://proposta.m4.digital';
    return `${baseUrl}/p/${hash}`;
  };

  const copiarLink = (hash: string) => {
    const link = getLinkProposta(hash);
    navigator.clipboard.writeText(link);
    alert('Link copiado para a área de transferência!');
  };

  const compartilharWhatsApp = (proposta: Proposta) => {
    const link = getLinkProposta(proposta.link_hash);
    const mensagem = `Olá! Segue o link da proposta M4 Digital para ${proposta.nome_empresa}: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  const abrirModalCompartilhar = (proposta: Proposta) => {
    setSelectedProposta(proposta);
    setShowShareModal(true);
  };

  const abrirModalEditar = (proposta: Proposta) => {
    setSelectedProposta(proposta);
    setShowEditModal(true);
  };

  const abrirModalCondicoes = (proposta: Proposta) => {
    setSelectedProposta(proposta);
    
    // Carregar condições existentes ou inicializar vazio
    const condicoesExistentes = proposta.condicoes_especiais?.planos_com_desconto || [];
    setCondicoes(condicoesExistentes);
    
    // Carregar data limite (formato YYYY-MM-DD para input type="date")
    let dataLimiteFormatada = '';
    if (proposta.data_limite_proposta) {
      // Converter de timestamp/datetime para YYYY-MM-DD
      const data = new Date(proposta.data_limite_proposta);
      dataLimiteFormatada = data.toISOString().split('T')[0];
    }
    console.log('📅 Data Limite carregada do banco:', proposta.data_limite_proposta);
    console.log('📅 Data Limite formatada para input:', dataLimiteFormatada);
    setDataLimite(dataLimiteFormatada);
    
    setShowCondicoesModal(true);
  };

  const salvarCondicoes = async () => {
    if (!selectedProposta) return;

    try {
      console.log('💾 Salvando condições...');
      console.log('📅 Data Limite a salvar:', dataLimite);
      console.log('🏷️ Condições:', condicoes);
      
      const { data, error } = await supabasePropostas
        .from('propostas')
        .update({
          condicoes_especiais: condicoes.length > 0 ? { planos_com_desconto: condicoes } : null,
          data_limite_proposta: dataLimite || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProposta.id)
        .select();

      console.log('✅ Resposta do update:', { data, error });

      if (error) throw error;

      alert('Condições especiais salvas com sucesso!');
      setShowCondicoesModal(false);
      carregarPropostas();
    } catch (error) {
      console.error('❌ Erro ao salvar condições:', error);
      alert('Erro ao salvar condições especiais');
    }
  };

  const adicionarCondicao = (plano: any, tipo: 'trafego' | 'crm') => {
    const identificador = tipo === 'trafego' ? plano.tipo : plano.nome;
    const plano_nome = tipo === 'trafego' ? plano.titulo : plano.nome;
    const valor_original = plano.preco_numerico;

    // Verificar se já existe
    const existe = condicoes.find(c => c.tipo === tipo && c.identificador === identificador);
    if (existe) {
      alert('Este plano já possui condição especial');
      return;
    }

    const novaCondicao: CondicaoEspecial = {
      tipo,
      identificador,
      plano_nome,
      valor_original,
      valor_desconto: valor_original,
      aplicar_primeiros_meses: null,
      descricao: ''
    };

    setCondicoes([...condicoes, novaCondicao]);
  };

  const removerCondicao = (index: number) => {
    setCondicoes(condicoes.filter((_, i) => i !== index));
  };

  const atualizarCondicao = (index: number, campo: keyof CondicaoEspecial, valor: any) => {
    const novasCondicoes = [...condicoes];
    novasCondicoes[index] = { ...novasCondicoes[index], [campo]: valor };
    setCondicoes(novasCondicoes);
  };

  const gerarSugestoesIA = async (index: number, condicao: CondicaoEspecial) => {
    setLoadingIA(true);
    setCondicaoAtualIA({ index, condicao });
    
    try {
      // Determinar se é plano recorrente
      let plano: any = null;
      let recorrente = false;
      
      if (condicao.tipo === 'trafego' && selectedProposta) {
        plano = selectedProposta.planos_trafego?.find((p: any) => p.tipo === condicao.identificador);
        recorrente = plano?.recorrente ?? true; // Assumir recorrente se não especificado
      } else if (condicao.tipo === 'crm' && selectedProposta) {
        plano = selectedProposta.planos_crm?.find((p: any) => p.nome === condicao.identificador);
        recorrente = true; // CRM é sempre recorrente
      }
      
      const response = await fetch('/api/propostas/gerar-descricao-ia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: condicao.tipo,
          plano_nome: condicao.plano_nome,
          valor_original: condicao.valor_original,
          valor_desconto: condicao.valor_desconto,
          aplicar_primeiros_meses: condicao.aplicar_primeiros_meses,
          recorrente
        })
      });
      
      const data = await response.json();
      
      if (data.sugestoes && data.sugestoes.length > 0) {
        setSugestoesIA(data.sugestoes);
        setShowSugestoesModal(true);
      } else {
        alert('Não foi possível gerar sugestões. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao gerar sugestões:', error);
      alert('Erro ao gerar sugestões com IA. Tente novamente.');
    } finally {
      setLoadingIA(false);
    }
  };

  const selecionarSugestaoIA = (sugestao: string) => {
    if (condicaoAtualIA) {
      atualizarCondicao(condicaoAtualIA.index, 'descricao', sugestao);
      setShowSugestoesModal(false);
      setCondicaoAtualIA(null);
    }
  };

  const excluirProposta = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta proposta?')) return;

    try {
      const { error } = await supabasePropostas
        .from('propostas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      alert('Proposta excluída com sucesso!');
      carregarPropostas();
    } catch (error) {
      console.error('Erro ao excluir proposta:', error);
      alert('Erro ao excluir proposta');
    }
  };

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Propostas Comerciais</h1>
        <p className="text-gray-600">Gerencie todas as propostas geradas pelo sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Busca */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por empresa
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome da empresa..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtro Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="todos">Todos os status</option>
              <option value="ativa">Ativa</option>
              <option value="visualizada">Visualizada</option>
              <option value="aceita">Aceita</option>
              <option value="recusada">Recusada</option>
            </select>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-800">{propostas.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aceitas</p>
              <p className="text-2xl font-bold text-green-600">
                {propostas.filter(p => p.status === 'aceita').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Visualizadas</p>
              <p className="text-2xl font-bold text-blue-600">
                {propostas.filter(p => p.status === 'visualizada').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-eye-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Recusadas</p>
              <p className="text-2xl font-bold text-red-600">
                {propostas.filter(p => p.status === 'recusada').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-close-line text-2xl text-red-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Carregando propostas...</p>
          </div>
        ) : filteredPropostas.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600 text-lg mb-2">Nenhuma proposta encontrada</p>
            <p className="text-gray-500 text-sm">
              {searchTerm || statusFilter !== 'todos' 
                ? 'Tente ajustar os filtros de busca' 
                : 'As propostas geradas aparecerão aqui'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visualizações
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Investimento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Criação
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPropostas.map((proposta) => (
                  <tr key={proposta.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{proposta.nome_empresa}</div>
                      <div className="text-sm text-gray-500">
                        {proposta.planos_trafego?.length || 0} tráfego • {proposta.planos_crm?.length || 0} CRM
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(proposta.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <i className="ri-eye-line text-gray-400"></i>
                        <span className="text-gray-900">{proposta.visualizacoes}</span>
                      </div>
                      {proposta.ultima_visualizacao && (
                        <div className="text-xs text-gray-500 mt-1">
                          Última: {new Date(proposta.ultima_visualizacao).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {proposta.total_unico > 0 && (
                        <div className="text-sm text-gray-900">
                          R$ {proposta.total_unico.toFixed(2).replace('.', ',')} único
                        </div>
                      )}
                      {proposta.total_mensal > 0 && (
                        <div className="text-sm text-gray-900">
                          R$ {proposta.total_mensal.toFixed(2).replace('.', ',')}/mês
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(proposta.created_at).toLocaleDateString('pt-BR')}
                      <div className="text-xs text-gray-400">
                        {new Date(proposta.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => window.open(getLinkProposta(proposta.link_hash), '_blank')}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => abrirModalCompartilhar(proposta)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Compartilhar"
                        >
                          <i className="ri-share-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => abrirModalCondicoes(proposta)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Condições Especiais"
                        >
                          <i className="ri-price-tag-3-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => abrirModalEditar(proposta)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => excluirProposta(proposta.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Compartilhar */}
      {showShareModal && selectedProposta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Compartilhar Proposta</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Proposta para: <span className="font-bold">{selectedProposta.nome_empresa}</span>
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-600 mb-2">Link da Proposta:</p>
                <p className="text-sm font-mono text-gray-800 break-all">
                  {getLinkProposta(selectedProposta.link_hash)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => copiarLink(selectedProposta.link_hash)}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg transition-colors"
              >
                <i className="ri-file-copy-line"></i>
                Copiar Link
              </button>
              <button
                onClick={() => compartilharWhatsApp(selectedProposta)}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Condições Especiais */}
      {showCondicoesModal && selectedProposta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">Condições Especiais</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedProposta.nome_empresa}</p>
              </div>
              <button
                onClick={() => setShowCondicoesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Data Limite da Proposta */}
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="ri-calendar-line mr-2"></i>
                Data Limite da Proposta
              </label>
              <input
                type="date"
                value={dataLimite}
                onChange={(e) => setDataLimite(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="dd/mm/aaaa"
              />
              {!dataLimite && (
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <i className="ri-alert-line"></i>
                  Nenhuma data limite cadastrada. Defina uma data e clique em "Salvar Condições".
                </p>
              )}
              {dataLimite && (
                <p className="text-xs text-gray-600 mt-2">
                  Após esta data, as condições especiais não serão mais válidas
                </p>
              )}
            </div>

            {/* Planos de Tráfego */}
            {selectedProposta.planos_trafego && selectedProposta.planos_trafego.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Planos de Tráfego</h4>
                <div className="space-y-3">
                  {selectedProposta.planos_trafego.map((plano: any, idx: number) => {
                    const condicaoExistente = condicoes.find(
                      c => c.tipo === 'trafego' && c.identificador === plano.tipo
                    );
                    return (
                      <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h5 className="font-semibold text-gray-800">{plano.titulo}</h5>
                            <p className="text-sm text-gray-600">
                              Valor original: {plano.preco}
                            </p>
                          </div>
                          {!condicaoExistente ? (
                            <button
                              onClick={() => adicionarCondicao(plano, 'trafego')}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                            >
                              <i className="ri-add-line mr-1"></i>
                              Adicionar Desconto
                            </button>
                          ) : (
                            <button
                              onClick={() => removerCondicao(condicoes.indexOf(condicaoExistente))}
                              className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm"
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Remover
                            </button>
                          )}
                        </div>
                        
                        {condicaoExistente && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Valor com Desconto (R$)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={condicaoExistente.valor_desconto}
                                  onChange={(e) => atualizarCondicao(
                                    condicoes.indexOf(condicaoExistente),
                                    'valor_desconto',
                                    parseFloat(e.target.value)
                                  )}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  % Desconto
                                </label>
                                <input
                                  type="text"
                                  value={`${(((condicaoExistente.valor_original - condicaoExistente.valor_desconto) / condicaoExistente.valor_original) * 100).toFixed(1)}%`}
                                  disabled
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Aplicar nos primeiros X meses (deixe vazio para desconto permanente)
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={condicaoExistente.aplicar_primeiros_meses || ''}
                                onChange={(e) => atualizarCondicao(
                                  condicoes.indexOf(condicaoExistente),
                                  'aplicar_primeiros_meses',
                                  e.target.value ? parseInt(e.target.value) : null
                                )}
                                placeholder="Ex: 3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Descrição da Condição
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={condicaoExistente.descricao}
                                  onChange={(e) => atualizarCondicao(
                                    condicoes.indexOf(condicaoExistente),
                                    'descricao',
                                    e.target.value
                                  )}
                                  placeholder="Ex: Desconto nos 3 primeiros meses"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => gerarSugestoesIA(condicoes.indexOf(condicaoExistente), condicaoExistente)}
                                  disabled={loadingIA}
                                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Gerar sugestões com IA"
                                >
                                  {loadingIA ? (
                                    <i className="ri-loader-4-line animate-spin"></i>
                                  ) : (
                                    <i className="ri-sparkling-line"></i>
                                  )}
                                  IA
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Planos CRM */}
            {selectedProposta.planos_crm && selectedProposta.planos_crm.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Planos LOVOO CRM</h4>
                <div className="space-y-3">
                  {selectedProposta.planos_crm.map((plano: any, idx: number) => {
                    const condicaoExistente = condicoes.find(
                      c => c.tipo === 'crm' && c.identificador === plano.nome
                    );
                    return (
                      <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h5 className="font-semibold text-gray-800">{plano.nome}</h5>
                            <p className="text-sm text-gray-600">
                              Valor original: {plano.preco}
                            </p>
                          </div>
                          {!condicaoExistente ? (
                            <button
                              onClick={() => adicionarCondicao(plano, 'crm')}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                            >
                              <i className="ri-add-line mr-1"></i>
                              Adicionar Desconto
                            </button>
                          ) : (
                            <button
                              onClick={() => removerCondicao(condicoes.indexOf(condicaoExistente))}
                              className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm"
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Remover
                            </button>
                          )}
                        </div>
                        
                        {condicaoExistente && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Valor com Desconto (R$)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={condicaoExistente.valor_desconto}
                                  onChange={(e) => atualizarCondicao(
                                    condicoes.indexOf(condicaoExistente),
                                    'valor_desconto',
                                    parseFloat(e.target.value)
                                  )}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  % Desconto
                                </label>
                                <input
                                  type="text"
                                  value={`${(((condicaoExistente.valor_original - condicaoExistente.valor_desconto) / condicaoExistente.valor_original) * 100).toFixed(1)}%`}
                                  disabled
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Aplicar nos primeiros X meses (deixe vazio para desconto permanente)
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={condicaoExistente.aplicar_primeiros_meses || ''}
                                onChange={(e) => atualizarCondicao(
                                  condicoes.indexOf(condicaoExistente),
                                  'aplicar_primeiros_meses',
                                  e.target.value ? parseInt(e.target.value) : null
                                )}
                                placeholder="Ex: 3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Descrição da Condição
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={condicaoExistente.descricao}
                                  onChange={(e) => atualizarCondicao(
                                    condicoes.indexOf(condicaoExistente),
                                    'descricao',
                                    e.target.value
                                  )}
                                  placeholder="Ex: Desconto de lançamento"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => gerarSugestoesIA(condicoes.indexOf(condicaoExistente), condicaoExistente)}
                                  disabled={loadingIA}
                                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Gerar sugestões com IA"
                                >
                                  {loadingIA ? (
                                    <i className="ri-loader-4-line animate-spin"></i>
                                  ) : (
                                    <i className="ri-sparkling-line"></i>
                                  )}
                                  IA
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Botões de Ação */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowCondicoesModal(false)}
                className="px-6 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarCondicoes}
                className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
              >
                <i className="ri-save-line mr-2"></i>
                Salvar Condições
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {showEditModal && selectedProposta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Editar Proposta</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Empresa
                </label>
                <input
                  type="text"
                  value={selectedProposta.nome_empresa}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50">
                    {getStatusBadge(selectedProposta.status)}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visualizações
                  </label>
                  <input
                    type="text"
                    value={selectedProposta.visualizacoes}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Planos de Tráfego ({selectedProposta.planos_trafego?.length || 0})
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-40 overflow-y-auto">
                  {selectedProposta.planos_trafego?.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedProposta.planos_trafego.map((plano: any, idx: number) => (
                        <li key={idx} className="text-sm text-gray-700">
                          • {plano.titulo} - {plano.preco}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum plano selecionado</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Planos CRM ({selectedProposta.planos_crm?.length || 0})
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-40 overflow-y-auto">
                  {selectedProposta.planos_crm?.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedProposta.planos_crm.map((plano: any, idx: number) => (
                        <li key={idx} className="text-sm text-gray-700">
                          • {plano.nome} - {plano.preco}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum plano selecionado</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <i className="ri-information-line mr-1"></i>
                  A edição completa de propostas será implementada em uma próxima versão.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Sugestões de IA */}
      {showSugestoesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-sparkling-line text-purple-600"></i>
                Sugestões de IA
              </h4>
              <button
                onClick={() => setShowSugestoesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <p className="text-xs text-gray-600 mb-4">
              Clique em uma sugestão para usar ou gere novas opções:
            </p>
            
            <div className="space-y-2 mb-4">
              {sugestoesIA.map((sugestao, idx) => (
                <button
                  key={idx}
                  onClick={() => selecionarSugestaoIA(sugestao)}
                  className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg transition-colors text-sm"
                >
                  {sugestao}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => condicaoAtualIA && gerarSugestoesIA(condicaoAtualIA.index, condicaoAtualIA.condicao)}
              disabled={loadingIA}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loadingIA ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Gerando...
                </>
              ) : (
                <>
                  <i className="ri-refresh-line"></i>
                  Gerar novas sugestões
                </>
              )}
            </button>
          </div>
        </div>
      )}
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
